import { Component, OnDestroy, OnInit, Renderer2, RendererFactory2, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';

import { AuthHelperService } from 'app/auth/helpers/auth-helper.service';
import { GlobalService } from 'app/global/global.service';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { SigningStatusEnum } from 'app/main/signing-pages/enums/enum';
import { SingingPageService } from 'app/main/signing-pages/signing-page/signing-page-service';
import { SigningPageComponent } from 'app/main/signing-pages/signing-page/signing-page.component';
import { RoleDetail } from 'app/main/signing-pages/signing-page/signing-page.dto';
import * as pdfjsLib from 'pdfjs-dist';
import { Subscription } from 'rxjs';
import { TranslationsHelperService } from '../../../../../translations.helper.service';
import { BrandingService } from '../../settings/branding/branding.service';
import { environment } from 'environments/environment';
import { TokenHelperService } from 'app/global/token-helper.service';

@Component({
  selector: 'app-view-document-detail',
  templateUrl: './view-document-detail.component.html',
  styleUrl: './view-document-detail.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [SigningPageComponent]
})
export class ViewDocumentDetailComponent implements OnInit, OnDestroy {

  public pdfPages: HTMLImageElement[] = [];
  public roleId: string = '';
  public roleDetail: RoleDetail;
  public templateContent: SafeHtml = '';
  public timeOutSubscription: Subscription;
  private renderer: Renderer2;
  public loadingSigningArea: boolean = true;
  public is_sender: number = 0;
  public signingStatusEnum = SigningStatusEnum;
  public uploadedFileType = 'pdf';




  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private _coreSidebarService: CoreSidebarService,
    private authHelper: AuthHelperService,
    private singingPage: SingingPageService,
    private globalService: GlobalService,
    rendererFactory: RendererFactory2,
    private signingPage: SigningPageComponent,
    private _translationsHelperService: TranslationsHelperService,
    private _sharedStoreFacade$: SharedStoreFacade,
    private titleService: Title,
    private brandingService:BrandingService,
    private tokenHelperService: TokenHelperService
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.authHelper.setLayoutConfig(true, false, false);
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/libs/pdf.worker.min.js';
  }

  ngOnInit(): void {
    this.roleId = this.route.snapshot.paramMap.get('id');
    this.tokenHelperService.saveSigningPageToken(this.route.snapshot.paramMap.get('token'), this.roleId);
    this.initDocumentDetailView();
  }



  /**
   * Initializes the document detail view.
   * @returns {void}
   * @memberof ViewDocumentDetailComponent
   * 
   * @description This method initializes the document detail view by fetching the document detail from the API.
   * It subscribes to the API response and handles the document detail response, errors, and finalization of the view.
   * 
   * @see SingingPageService.getDocumentDetail
   * @see handleDocumentDetail
   * @see handleErrors
   * @see finalizeDocumentDetailView
   */
  initDocumentDetailView(): void {
    this.singingPage.getDocumentDetail(Number(this.roleId)).subscribe({
      next: (detail: RoleDetail) => this.handleDocumentDetail(detail),
      error: (error: string) => this.handleErrors(error),
      complete: () => this.finalizeDocumentDetailView(),
    });
  }


  /**
   * Handles the document detail response from the API.
   * @param detail - The document detail response from the API
   * @returns {void}
   * @private
   * @memberof ViewDocumentDetailComponent
   * 
   * @description This method sets the necessary details for the document detail view.
   * It sets the language, page title, favicon, template content, and other necessary details.
   * It also handles the rendering of the PDF document if it has been uploaded.
   * 
   */
  private handleDocumentDetail(detail: RoleDetail): void {
    this.roleDetail = detail;
    const { roleable, brand, recipient_role_detail, uploaded_pdf } = detail;
  
    // Set language
    const language = this.globalService.shortLangValue(roleable.language);
    this._translationsHelperService.changeLanguage(language);
  
    // Set page title and favicon
    if (brand?.title) {
      this.titleService.setTitle(brand.title);
    }

    if (brand?.favicon) {
      this.changeFavicon(this.brandingService.getBrandingImagesPath(brand.favicon, Number(brand.id)));
    }
  
    // Set other necessary details
    this.is_sender = recipient_role_detail.is_sender;
    this.globalService.fields_data = roleable.fields_datas?.data ?? {};
  
    // Handle PDF rendering
    if ((roleable.status === SigningStatusEnum.UPLOADED || roleable.is_uploaded) && uploaded_pdf) {
      this.renderPdfOrImage(uploaded_pdf);
    } else {
      this.templateContent = this.sanitizer.bypassSecurityTrustHtml(roleable.content);
      this.loadingSigningArea = false;
    }
  
    // Save user timezone
    this._sharedStoreFacade$.saveUserTimezone(roleable.timezone);
  }


  /**
   * Finalizes the document detail view.
   * @returns {void}
   * @private
   * @memberof ViewDocumentDetailComponent
   * 
   * @description This method initializes the signing page view with the necessary details.
   * It sets the fields data, role detail, and view type for the signing page.
   * 
   */
  private finalizeDocumentDetailView(): void {
    this.signingPage.initView(this.globalService.fields_data, this.roleDetail, 'fullScreenView');
  }


  /**
   * Handles the errors that may occur during the loading of the document detail.
   * @param error - The error message to be displayed to the user.
   * @returns {void}
   * @private
   * @memberof ViewDocumentDetailComponent
   * 
   * @description This method displays an error message to the user and closes the sidebar.
   * 
   */
  private handleErrors(error: string): void {
    this.globalService.toastrError(error);
    this.globalService.toggleClass(document.body, 'sidebar-opened');
  }






  /**
   * Changes the favicon of the document to the specified URL.
   *
   * This method creates a new `<link>` element with the `rel` attribute set to 'icon',
   * the `type` attribute set to 'image/x-icon', and the `href` attribute set to the provided
   * favicon URL. It then removes any existing favicon from the document's `<head>` element
   * and appends the new favicon.
   *
   * @param faviconUrl - The URL of the new favicon to be set.
   */
  changeFavicon(faviconUrl: string): void {
    const link: HTMLLinkElement = this.renderer.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    link.href = faviconUrl;

    // Remove existing favicon if it exists
    const head = document.getElementsByTagName('head')[0];
    const existingFavicon = document.querySelector('link[rel="icon"]');
    if (existingFavicon) {
      head.removeChild(existingFavicon);
    }

    // Append new favicon to the head
    head.appendChild(link);
  }

  /**
   * Renders the Uploaded file specified in `this.roleDetail?.uploaded_pdf`.
   * 
   * This method uses the pdfjsLib library to load the PDF document and render each page onto a canvas.
   * Each rendered page is then converted to an image and pushed to the `this.pdfPages` array.
   * 
   * The method handles errors that may occur during the loading and rendering of the PDF document.
   * 
   * @returns {void}
   */
  renderPdfOrImage(uploaded_file: string) {
    this.pdfPages = []; // Reset the pages array
    this.loadingSigningArea = true;

    // Safely get extension after last dot
    const fileExtension = uploaded_file.substring(uploaded_file.lastIndexOf('.') + 1).toLowerCase();

    if (fileExtension === 'pdf') {
      this.uploadedFileType = 'pdf';

    const pdfPath = `${environment.apiUrl}sign-document/${this.roleId}/${this.roleDetail?.roleable_id}/pdf`;

    pdfjsLib.getDocument({
      url: pdfPath,
      httpHeaders: {
        'Token': this.route.snapshot.paramMap.get('token')
      }
    }).promise.then(pdf => {
      const numPages = pdf.numPages;
      this.pdfPages = new Array(numPages); // Pre-allocate array to preserve order

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        ((pageIndex => {
          pdf.getPage(pageIndex).then(page => {
            const viewport = page.getViewport({ scale: 2 });
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            page.render({
              canvasContext: context,
              viewport: viewport
            }).promise.then(() => {
              const img = new Image();
              img.src = canvas.toDataURL();
              this.pdfPages[pageIndex - 1] = img;
            }).catch(renderError => {
              console.warn(`Error rendering page ${pageIndex}:`, renderError);
            });
          }).catch(pageError => {
            console.warn(`Error loading page ${pageIndex}:`, pageError);
          });
        })(pageNum));
      }

      this.loadingSigningArea = false;
    }).catch(error => {
      console.warn('Error loading PDF:', error);
      this.loadingSigningArea = false;
    });
  } else if (['png', 'jpg', 'jpeg'].includes(fileExtension)) {
    this.uploadedFileType = 'img';
    this.loadingSigningArea = false;
  } else {
    console.warn(`Unsupported file type for rendering: ${fileExtension}`);
    this.loadingSigningArea = false;
  }
}

  /**
   * Toggle sidebar
   * @param name
   * 
   * @returns {void}
   * 
   * @description This method toggles the sidebar with the specified name. 
   */
  toggleSidebar(name: string): void {
    this._coreSidebarService.getSidebarRegistry(name).toggleOpen();
    this.globalService.toggleClass(document.body, 'sidebar-opened');
  }



  /**
   * Lifecycle hook that is called when the component is destroyed.
   * 
   * This method performs cleanup by unsubscribing from the `timeOutSubscription`
   * if it exists, to prevent memory leaks.
   */
  ngOnDestroy() {
    if (this.timeOutSubscription) {
      this.timeOutSubscription.unsubscribe();
    }
  }
}
